import type { CashSummaryDto } from "@grizzly/shared";
import { formatAmount, formatDateTime } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Card } from "../ui/card.js";
import { DataTable } from "../ui/dataTable.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";

interface LedgerRow {
  id: string;
  when: string;
  direction: "in" | "out";
  amount: number;
  category: string;
  reference: string;
}

export function CashLedger({ summary }: { summary: CashSummaryDto | null }) {
  const { t } = useI18n();
  if (!summary) return null;

  const rows: LedgerRow[] = summary.transactions.map((t) => ({
    id: t.id,
    when: formatDateTime(t.occurredAt),
    direction: t.direction,
    amount: t.amountMinor,
    category: t.categoryName ?? t.description ?? "-",
    reference: t.reference ?? "-",
  }));

  return (
    <>
      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-ink-muted">{t("finance.moneyIn")}</p>
            <p className="tabular text-lg font-semibold text-status-complete">
              {formatAmount(summary.totalInMinor)}
            </p>
          </div>
          <div>
            <p className="text-xs text-ink-muted">{t("finance.moneyOut")}</p>
            <p className="tabular text-lg font-semibold text-status-risk">
              {formatAmount(summary.totalOutMinor)}
            </p>
          </div>
          <div>
            <p className="text-xs text-ink-muted">{t("finance.net")}</p>
            <p className="tabular text-lg font-semibold text-ink">
              {formatAmount(summary.netMinor)}
            </p>
          </div>
        </div>
      </Card>
      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink">{t("finance.cashLedger")}</h2>
        <DataTable
          caption={t("finance.cashLedger")}
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            { key: "when", header: t("finance.when"), render: (r) => r.when },
            {
              key: "direction",
              header: t("finance.direction"),
              render: (r) => (
                <Badge tone={r.direction === "in" ? "complete" : "risk"}>
                  {t(`status.direction.${r.direction}` as MessageKey)}
                </Badge>
              ),
            },
            {
              key: "amount",
              header: t("common.fields.amount"),
              render: (r) => <span className="tabular">{formatAmount(r.amount)}</span>,
            },
            {
              key: "category",
              header: t("common.fields.category"),
              render: (r) => r.category,
            },
            {
              key: "reference",
              header: t("common.fields.reference"),
              render: (r) => r.reference,
            },
          ]}
          emptyTitle={t("finance.noCashMovements")}
          emptyDescription={t("finance.noCashMovementsDesc")}
        />
      </section>
    </>
  );
}
